import { DadosMilitaresService } from '../../core/services/dados-militares.service';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { EtapasBeneficioComponent } from '../../shared/components/etapas-beneficio/etapas-beneficio.component';
import { SessionService } from '../../core/services/session.service';
import { DocumentoAnexado, DocumentosService } from '../../core/services/documentos.service';
import { ProcessoService } from '../../core/services/processo.service';
import { AssinadorService } from '../../core/services/assinador.service';
import { WebPkiService } from '../../core/services/web-pki.service';
import {
  ResumoAssinatura,
  mensagemResumoAssinatura,
  motivoDoErro,
} from '../../core/services/assinatura-lote.service';
import { SelecionarCertificadoDialogComponent } from './selecionar-certificado-dialog.component';
import { ConfirmDialogComponent } from '../processo-detalhe/confirm-dialog.component';
import {
  Documento,
  TipoBeneficio,
  TIPOS_BENEFICIO,
  DOCUMENTOS_POR_TIPO,
} from '../../core/models/documento.model';

/**
 * Documento conferido na abertura que não corresponde a nenhum item do checklist da hipótese
 * (ex.: o laudo e o requerimento enviados na tela de requerimento, os comprovantes das abas 4 a 6).
 * A abertura exige que ele também esteja assinado, então precisa ficar visível na aba 9.
 */
export interface OutroDocumentoAnexado {
  id: number;
  nome: string;
  arquivo?: string;
  dataUpload?: string;
  status: 'presente';
  hash?: string;
  hashAssinado?: string;
  assinado?: boolean;
  signing?: boolean;
  uploading?: boolean;
}

/** O que a assinatura e o download precisam de um documento da tela (item do checklist ou "outro"). */
type DocumentoDaTela = Pick<Documento, 'nome' | 'status' | 'arquivo' | 'hash' | 'hashAssinado' | 'assinado' | 'signing'>;

@Component({
  selector: 'app-anexar-documentos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDialogModule,
    EtapasBeneficioComponent,
  ],
  templateUrl: './anexar-documentos.component.html',
  styleUrl: './anexar-documentos.component.scss',
})
export class AnexarDocumentosComponent implements OnInit {
  beneficiario: any;
  loading = false;

  tiposBeneficio: TipoBeneficio[] = TIPOS_BENEFICIO;
  tipoBeneficioSelecionado: string = '';
  documentos: Documento[] = [];
  contextoMilitar:any={aplicabilidadeChecklist:{}};

  displayedColumns: string[] = ['nome', 'categoria', 'status', 'assinado', 'acoes'];

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  documentoParaUpload: Documento | null = null;

  private idVinculo: number | null = null;

  /** Documentos da lista pré-processo que não casam com nenhum item do checklist; ficam fora da assinatura em lote. */
  outrosDocumentos: OutroDocumentoAnexado[] = [];
  colunasOutros: string[] = ['nome', 'arquivo', 'assinado', 'acoes'];

  /** Preenchido quando a lista de documentos anexados não pôde ser carregada (a tela não sabe o que já foi enviado). */
  erroAnexados: string | null = null;

  // Estado da assinatura em lote
  public assinandoLote = false;
  public progressoAssinatura = { atual: 0, total: 0 };

  constructor(
    private dadosMilitares: DadosMilitaresService,
    private sessionService: SessionService,
    private documentosService: DocumentosService,
    private processoService: ProcessoService,
    private assinadorService: AssinadorService,
    private webPkiService: WebPkiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.beneficiario = this.sessionService.beneficiario;
    this.carregarVinculo();
    this.dadosMilitares.hipoteses().subscribe({next:h=>this.tiposBeneficio=h,error:()=>this.snackBar.open('Catálogo de hipóteses indisponível.','Fechar',{duration:5000})});
    // Sem hipótese (ou sem os dados militares) não há checklist, mas a lista é carregada para "Outros".
    this.dadosMilitares.buscar(this.beneficiario.matricula,this.beneficiario.orgao.id).subscribe({next:d=>{this.contextoMilitar=d ?? {aplicabilidadeChecklist:{}};this.contextoMilitar.aplicabilidadeChecklist ??={};this.tipoBeneficioSelecionado=d?.hipoteseChecklist ?? '';if(this.tipoBeneficioSelecionado)this.onTipoBeneficioChange();else this.carregarDocumentosAnexados();},error:()=>this.carregarDocumentosAnexados()});
  }

  private logTiposDocumentoDisponiveis(): void {
    this.documentosService.listarTiposDocumento().subscribe({
      next: (tipos) => {
        console.log('[anexar-documentos] Tipos de documento cadastrados no banco:', tipos);
        console.table(tipos);
      },
      error: (err) => {
        console.error('[anexar-documentos] Falha ao listar tipos de documento:', err);
      },
    });
  }

  private carregarVinculo(): void {
    const matricula = this.beneficiario?.matricula;
    const idOrgao = this.beneficiario?.orgao?.id;
    if (!matricula || !idOrgao) return;

    // O vínculo serve só ao upload (idOrigem); a lista de documentos vem por matrícula/órgão.
    this.processoService.getDadosAbertura(matricula, idOrgao).subscribe({
      next: (res) => {
        this.idVinculo = res?.vinculo?.id ?? null;
      },
      error: (err) => {
        console.error('Falha ao buscar dados de abertura', err);
        this.snackBar.open(
          'Não foi possível identificar o vínculo do militar. Documentos serão enviados sem vinculação ao processo.',
          'Fechar',
          { duration: 5000, horizontalPosition: 'right', verticalPosition: 'bottom' }
        );
      },
    });
  }

  /**
   * Mesma lista que a abertura confere (GET /files/lista-pre-processo): inclui os comprovantes das
   * abas 4 a 6 e o requerimento antigo sem origem, que o lista-por-vinculo não trazia.
   */
  private carregarDocumentosAnexados(): void {
    const matricula = this.beneficiario?.matricula;
    const idOrgao = this.beneficiario?.orgao?.id;
    if (!matricula || !idOrgao) return;
    this.documentosService.listarPreProcesso(matricula, idOrgao).subscribe({
      next: (anexados) => {
        this.erroAnexados = null;
        this.distribuirAnexos(anexados ?? []);
      },
      error: (err) => {
        // Mantém o que a tela já mostrava (lista anterior) e deixa claro que nada foi verificado agora:
        // antes todos os itens ficavam "Ausente" em silêncio.
        console.error('Falha ao listar documentos anexados', err);
        const motivo = motivoDoErro(err);
        this.erroAnexados = `Não foi possível carregar os documentos anexados: ${motivo}. ` +
          'O status dos documentos não foi verificado; recarregue a página.';
        this.snackBar.open(`Não foi possível carregar os documentos anexados: ${motivo}. Recarregue a página.`, 'Fechar', {
          duration: 10000, horizontalPosition: 'right', verticalPosition: 'bottom',
        });
      },
    });
  }

  /**
   * Casa cada item do checklist com um documento da lista, na mesma base da abertura (que confere por
   * id): primeiro pelo id do próprio tipo, depois pelos ids equivalentes do item (ex.: o laudo tipo 47
   * enviado na tela de requerimento vale para o item do laudo pericial); só então, para os itens ainda
   * sem anexo, pelo nome do tipo e pelos nomes equivalentes. Cada anexo atende a um item só, e os que
   * sobram vão para "Outros documentos anexados": antes eles ficavam invisíveis aqui e travavam a
   * abertura por "não assinados".
   */
  private distribuirAnexos(anexados: DocumentoAnexado[]): void {
    const livres = [...anexados];
    // Entre anexos que casam com o mesmo item prevalece o último da lista, como no mapa por nome de antes.
    const retirar = (casa: (a: DocumentoAnexado) => boolean): DocumentoAnexado | undefined => {
      for (let i = livres.length - 1; i >= 0; i--) {
        if (casa(livres[i])) return livres.splice(i, 1)[0];
      }
      return undefined;
    };
    const anexoDoItem = new Map<Documento, DocumentoAnexado>();
    // Cada critério percorre todos os itens antes do próximo: um anexo do próprio tipo de um item
    // não é tomado por outro item via nome ou equivalência.
    const casarItens = (casa: (doc: Documento, a: DocumentoAnexado) => boolean): void => {
      for (const doc of this.documentos) {
        if (anexoDoItem.has(doc)) continue;
        const a = retirar((x) => casa(doc, x));
        if (a) anexoDoItem.set(doc, a);
      }
    };
    casarItens((doc, x) => doc.idTipoDocumento != null && x.idTipoDocumento === doc.idTipoDocumento);
    casarItens((doc, x) => x.idTipoDocumento != null && (doc.idsEquivalentes ?? []).includes(x.idTipoDocumento));
    casarItens((doc, x) => !!x.tipoDocumentoNome && x.tipoDocumentoNome === doc.tipoDocumento);
    casarItens((doc, x) => !!x.tipoDocumentoNome && (doc.nomesEquivalentes ?? []).includes(x.tipoDocumentoNome));

    anexoDoItem.forEach((a, doc) => {
      doc.status = 'presente';
      doc.arquivo = a.nomeArquivo;
      doc.dataUpload = a.dataGeracao;
      doc.hash = a.hash;
      doc.assinado = a.assinado;
      doc.hashAssinado = a.hashAssinado;
    });
    this.outrosDocumentos = livres.map((a) => ({
      id: a.id,
      nome: a.tipoDocumentoNome || a.nomeArquivo || 'Documento',
      arquivo: a.nomeArquivo,
      dataUpload: a.dataGeracao,
      status: 'presente' as const,
      hash: a.hash,
      hashAssinado: a.hashAssinado,
      assinado: a.assinado,
    }));
  }

  // ====== ASSINATURA ======

  public getDocumentosPendentesAssinatura(): Documento[] {
    return this.documentos.filter((d) => d.status === 'presente' && d.hash && !d.assinado);
  }

  public async assinarPendentes(): Promise<void> {
    console.log('[assinatura] === INÍCIO assinarPendentes ===');
    const pendentes = this.getDocumentosPendentesAssinatura();
    console.log(`[assinatura] ${pendentes.length} documento(s) pendente(s):`, pendentes);
    if (pendentes.length === 0) {
      console.warn('[assinatura] Nada a assinar — todos já estão assinados ou nenhum foi anexado ainda.');
      this.snackBar.open('Nenhum documento pendente de assinatura.', 'Fechar', {
        duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom',
      });
      return;
    }
    await this.assinarDocumentos(pendentes);
  }

  /** Assina um documento de "Outros documentos anexados": fica fora do lote e é assinado um a um. */
  public async assinarOutroDocumento(doc: OutroDocumentoAnexado): Promise<void> {
    if (this.assinandoLote || doc.signing || doc.assinado || !doc.hash) return;
    await this.assinarDocumentos([doc]);
  }

  private async assinarDocumentos(pendentes: DocumentoDaTela[]): Promise<void> {
    const guardiao: any = this.lerGuardiao();
    console.log('[assinatura] guardiao recebido:', guardiao);
    console.log('[assinatura] sessionStorage.guardiao:', sessionStorage.getItem('guardiao'));

    // Fallback: se nao tiver guardiao (teste local sem SGPREV), usa 'GESTOR' como nome
    const nomeUsuario: string =
      guardiao?.nome ??
      guardiao?.nomeUsuario ??
      guardiao?.usuario?.nome ??
      'GESTOR';

    let certContent: string;
    let thumbprint: string;
    try {
      const cert = await this.selecionarCertificado();
      if (!cert) return; // usuário cancelou
      thumbprint = cert.thumbprint;
      certContent = cert.certContent;
    } catch (err: any) {
      this.snackBar.open(
        err?.message ?? 'Falha ao acessar certificados.',
        'Fechar',
        { duration: 6000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
      return;
    }

    this.assinandoLote = true;
    this.progressoAssinatura = { atual: 0, total: pendentes.length };

    const resumo: ResumoAssinatura = { sucesso: [], falha: [] };

    for (const doc of pendentes) {
      this.progressoAssinatura.atual++;
      doc.signing = true;
      console.log(`[assinatura] iniciando "${doc.nome}" — hash=${doc.hash}`);
      try {
        await this.assinarDocumento(doc, thumbprint, certContent, nomeUsuario, guardiao);
        doc.signing = false;
        doc.assinado = true;
        resumo.sucesso.push(doc.nome);
        console.log(`[assinatura] ✅ "${doc.nome}" assinado com sucesso`);
      } catch (err: any) {
        doc.signing = false;
        resumo.falha.push({ nome: doc.nome, motivo: motivoDoErro(err) });
        console.error(`[assinatura] ❌ falha em "${doc.nome}":`, {
          status: err?.status,
          statusText: err?.statusText,
          backendMessage: err?.error?.message ?? err?.error,
          url: err?.url,
          fullError: err,
        });
      }
    }

    this.assinandoLote = false;
    this.exibirResumoAssinatura(resumo);
  }

  private assinarDocumento(
    doc: DocumentoDaTela,
    thumbprint: string,
    certContent: string,
    nomeUsuario: string,
    guardiao: any
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!doc.hash) {
        reject({ message: 'Documento sem hash original.' });
        return;
      }
      this.assinadorService
        .start({ hashDocumento: doc.hash, certContent })
        .subscribe({
          next: (startRes) => {
            this.webPkiService.assinarHash(thumbprint, startRes.toSignHash).subscribe({
              next: (signature) => {
                this.assinadorService
                  .complete({
                    fileToSign: startRes.fileName,
                    signature,
                    transferFileId: startRes.transferFileId,
                    hashDocumento: doc.hash!,
                    nomeUsuario,
                    cpfUsuario: guardiao?.cpf ?? guardiao?.CPF ?? undefined,
                    perfilUsuario: guardiao?.grupo?.nome ?? 'GESTOR',
                  })
                  .subscribe({
                    next: (res) => { (doc as any).hashAssinado = res?.hashAssinado; resolve(); },
                    error: (err) => reject(err),
                  });
              },
              error: (err) => reject(err),
            });
          },
          error: (err) => reject(err),
        });
    });
  }

  private selecionarCertificado(): Promise<{ thumbprint: string; certContent: string } | null> {
    return new Promise((resolve, reject) => {
      console.log('[assinatura] etapa 1/4: inicializando Web PKI...');
      this.webPkiService.init().subscribe({
        next: () => {
          console.log('[assinatura] ✓ Web PKI inicializado, listando certificados...');
          this.webPkiService.listarCertificados().subscribe({
            next: (certs) => {
              console.log(`[assinatura] ✓ ${certs.length} certificado(s) encontrado(s):`, certs);
              if (certs.length === 0) {
                console.warn('[assinatura] Nenhum certificado disponível no dispositivo.');
              }
              const ref = this.dialog.open(SelecionarCertificadoDialogComponent, {
                data: { certificados: certs },
                autoFocus: false,
              });
              ref.afterClosed().subscribe((thumbprint: string | null | undefined) => {
                if (!thumbprint) {
                  console.warn('[assinatura] Usuário cancelou a seleção do certificado.');
                  resolve(null);
                  return;
                }
                console.log(`[assinatura] ✓ Certificado escolhido (thumbprint=${thumbprint}), lendo conteúdo...`);
                this.webPkiService.lerCertificado(thumbprint).subscribe({
                  next: (certContent) => {
                    console.log('[assinatura] ✓ Certificado lido. Pronto pra chamar /assinador/start.');
                    resolve({ thumbprint, certContent });
                  },
                  error: (err) => {
                    console.error('[assinatura] ✗ Falha ao ler certificado:', err);
                    reject(err);
                  },
                });
              });
            },
            error: (err) => {
              console.error('[assinatura] ✗ Falha ao listar certificados:', err);
              reject(err);
            },
          });
        },
        error: (err) => {
          console.error('[assinatura] ✗ Falha ao inicializar Web PKI:', err);
          reject(err);
        },
      });
    });
  }

  private exibirResumoAssinatura(resumo: ResumoAssinatura): void {
    // Com falhas, o aviso traz os motivos distintos devolvidos pelo servidor.
    this.snackBar.open(mensagemResumoAssinatura(resumo), 'Fechar', {
      duration: resumo.falha.length === 0 ? 5000 : 10000,
      horizontalPosition: 'right', verticalPosition: 'bottom',
    });
  }

  private lerGuardiao(): any {
    const g = this.sessionService.guardiao;
    if (!g) return null;
    if (typeof g === 'string') {
      try { return JSON.parse(g); } catch { return null; }
    }
    return g;
  }

  salvarChecklist():void {
    this.contextoMilitar.hipoteseChecklist=this.tipoBeneficioSelecionado;
    this.dadosMilitares.salvar(this.beneficiario.matricula,this.beneficiario.orgao.id,this.contextoMilitar).subscribe({next:d=>{this.contextoMilitar=d;this.snackBar.open('Hipótese e aplicabilidade salvas.','Fechar',{duration:3000});},error:()=>this.snackBar.open('Não foi possível salvar o checklist.','Fechar',{duration:5000})});
  }

  getTipoBeneficioDescricao(): string {
    const tipo = this.tiposBeneficio.find(t => t.id === this.tipoBeneficioSelecionado);
    return tipo ? tipo.descricao : '';
  }

  onTipoBeneficioChange(): void {
    if (this.tipoBeneficioSelecionado) {
      const tipo = this.tipoBeneficioSelecionado;
      this.contextoMilitar.hipoteseChecklist=tipo;
      this.loading = true;
      this.documentosService.listarExigidos(tipo).subscribe({
        next: (exigidos) => {
          this.documentos = (exigidos || [])
            .slice()
            .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
            .map((e: any) => ({
              codigo:e.codigo,condicao:e.condicao,
              id: e.ordem ?? e.idTipoDocumento,
              idTipoDocumento: e.idTipoDocumento,
              nome: e.nome,
              tipoDocumento: e.tipoDocumento,
              categoria: e.categoria,
              obrigatorio: e.obrigatorio,
              idsEquivalentes: Array.isArray(e.idsEquivalentes) ? e.idsEquivalentes : [],
              nomesEquivalentes: Array.isArray(e.nomesEquivalentes) ? e.nomesEquivalentes : [],
              status: 'ausente' as const,
            }));
          this.loading = false;
          this.carregarDocumentosAnexados();
        },
        error: (err) => {
          console.error('Falha ao buscar documentos exigidos no back, usando fallback local', err);
          this.documentos = [];
          this.snackBar.open('Não foi possível carregar o checklist oficial.','Fechar',{duration:5000});
          this.loading = false;
          this.snackBar.open(
            'Não foi possível carregar a lista de documentos do servidor. Exibindo lista padrão.',
            'Fechar',
            { duration: 4000, horizontalPosition: 'right', verticalPosition: 'bottom' }
          );
          this.carregarDocumentosAnexados();
        },
      });
    } else {
      this.documentos = [];
      // Sem checklist, todos os anexos vão para "Outros documentos anexados".
      this.carregarDocumentosAnexados();
    }
  }

  getDocumentosIdentificacao(): Documento[] {
    return this.documentos.filter((d) => d.categoria === 'identificacao');
  }

  getDocumentosFuncionais(): Documento[] {
    return this.documentos.filter((d) => d.categoria === 'funcional');
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'presente':
        return 'status-presente';
      case 'pendente':
        return 'status-pendente';
      default:
        return 'status-ausente';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'presente':
        return 'Anexado';
      case 'pendente':
        return 'Pendente';
      default:
        // Sem a lista de anexados não dá para afirmar que o documento está ausente.
        return this.erroAnexados ? 'Não verificado' : 'Ausente';
    }
  }

  getTotalAnexados(): number {
    return this.documentos.filter((d) => d.status === 'presente').length;
  }

  getTotalDocumentos(): number {
    return this.documentos.length;
  }

  triggerUpload(documento: Documento): void {
    this.documentoParaUpload = documento;
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0 || !this.documentoParaUpload) {
      return;
    }

    const file = input.files[0];
    const documento = this.documentoParaUpload;

    if (!this.beneficiario?.matricula || !this.beneficiario?.orgao?.id) {
      this.snackBar.open(
        'Dados do militar não carregados. Tente recarregar a página.',
        'Fechar',
        { duration: 4000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
      input.value = '';
      this.documentoParaUpload = null;
      return;
    }

    documento.uploading = true;
    documento.status = 'pendente';

    this.snackBar.open(
      `Enviando "${documento.nome}"...`,
      '',
      { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
    );

    this.documentosService
      .uploadDocumento(file, {
        matricula: this.beneficiario.matricula,
        idOrgao: this.beneficiario.orgao.id,
        idOrigem: this.idVinculo ?? undefined,
        tipoDocumento: documento.tipoDocumento,
        // Busca por id no backend: evita o 500 quando o nome do tipo está
        // duplicado na tabela (causa da falha do Comprovante de Endereço/Extrato).
        idTipoDocumento: (documento as any).idTipoDocumento ?? undefined,
      })
      .subscribe({
        next: (res: any) => {
          documento.uploading = false;

          // O backend responde falhas de negócio com HTTP 200 + mensagem
          // ("Tipo documento não existe.", "Documento já anexado.") — não
          // podemos assumir sucesso só pelo status HTTP.
          const mensagem: string = res?.message ?? '';
          const falhou = /n[aã]o existe|j[aá] anexado/i.test(mensagem);

          if (falhou) {
            documento.status = 'ausente';
            this.snackBar.open(
              `Não foi possível anexar "${documento.nome}": ${mensagem}`,
              'Fechar',
              { duration: 6000, horizontalPosition: 'right', verticalPosition: 'bottom' }
            );
          } else {
            documento.status = 'presente';
            documento.arquivo = file.name;
            documento.dataUpload = new Date().toISOString();
            this.snackBar.open(
              `Documento "${documento.nome}" enviado com sucesso!`,
              'Fechar',
              { duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom' }
            );
          }

          // Recarrega do backend: confirma o que foi de fato persistido e traz
          // o hash do documento (sem ele, download e assinatura não funcionam
          // até a página ser recarregada).
          this.carregarDocumentosAnexados();
        },
        error: (err: any) => {
          documento.uploading = false;
          documento.status = 'ausente';

          const detalhe = err?.error?.message ?? err?.message ?? 'Falha ao enviar documento';
          this.snackBar.open(
            `Erro: ${detalhe}`,
            'Fechar',
            { duration: 5000, horizontalPosition: 'right', verticalPosition: 'bottom' }
          );
        },
      });

    this.documentoParaUpload = null;
    input.value = '';
  }

  downloadDocumento(documento: DocumentoDaTela): void {
    if (documento.status !== 'presente') {
      this.snackBar.open('Documento nao anexado', 'Fechar', {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      });
      return;
    }

    const hashAssinado = (documento as any).hashAssinado;
    const baixarAssinado = documento.assinado === true && hashAssinado != null;
    const hash = baixarAssinado ? hashAssinado : documento.hash;
    if (!hash) {
      this.snackBar.open('Documento sem hash para download.', 'Fechar', {
        duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom',
      });
      return;
    }
    this.documentosService.download(hash).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documento.arquivo ?? `${documento.nome ?? 'documento'}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.snackBar.open('Falha ao baixar o documento.', 'Fechar', {
          duration: 5000, horizontalPosition: 'right', verticalPosition: 'bottom',
        });
      },
    });
  }

  excluirDocumento(documento: Documento): void {
    if (documento.status !== 'presente') {
      return;
    }

    const hash = documento.hash;
    if (!hash) {
      this.snackBar.open('Documento sem hash para excluir.', 'Fechar', {
        duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom',
      });
      return;
    }

    documento.uploading = true;
    this.documentosService.excluir(hash).subscribe({
      next: () => {
        documento.uploading = false;
        documento.status = 'ausente';
        documento.arquivo = undefined;
        documento.dataUpload = undefined;
        documento.hash = undefined;
        documento.assinado = false;
        (documento as any).hashAssinado = undefined;
        this.snackBar.open(`Documento "${documento.nome}" removido`, 'Fechar', {
          duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom',
        });
      },
      error: (err: any) => {
        documento.uploading = false;
        const detalhe = err?.error?.message ?? err?.message ?? 'Falha ao excluir documento';
        this.snackBar.open(`Erro: ${detalhe}`, 'Fechar', {
          duration: 5000, horizontalPosition: 'right', verticalPosition: 'bottom',
        });
      },
    });
  }

  /**
   * Exclui um documento de "Outros documentos anexados" depois de confirmar: ele pode ter vindo de outra
   * aba e também deixa de aparecer lá. O servidor recusa excluir documento assinado.
   */
  excluirOutroDocumento(doc: OutroDocumentoAnexado): void {
    if (doc.assinado) {
      this.snackBar.open('Documento assinado não pode ser excluído.', 'Fechar', {
        duration: 4000, horizontalPosition: 'right', verticalPosition: 'bottom',
      });
      return;
    }
    const hash = doc.hash;
    if (!hash || doc.uploading || doc.signing) return;

    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        titulo: 'Excluir documento',
        mensagem: `Deseja realmente excluir "${doc.arquivo || doc.nome}"? O documento também deixa de aparecer na aba ` +
          'em que foi enviado (Alterações Funcionais, Deduções, Acréscimos ou tela de requerimento).',
        textoConfirmar: 'Excluir',
      },
      autoFocus: false,
    });
    ref.afterClosed().subscribe((confirmado: boolean) => {
      if (!confirmado) return;
      doc.uploading = true;
      this.documentosService.excluir(hash).subscribe({
        next: () => {
          doc.uploading = false;
          this.outrosDocumentos = this.outrosDocumentos.filter((d) => d !== doc);
          this.snackBar.open(`Documento "${doc.arquivo ?? doc.nome}" removido`, 'Fechar', {
            duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom',
          });
        },
        error: (err: any) => {
          doc.uploading = false;
          this.snackBar.open(`Erro: ${motivoDoErro(err)}`, 'Fechar', {
            duration: 5000, horizontalPosition: 'right', verticalPosition: 'bottom',
          });
        },
      });
    });
  }

  salvar(): void {
    const anexados = this.getTotalAnexados();
    const total = this.getTotalDocumentos();

    if (anexados === 0) {
      this.snackBar.open('Nenhum documento foi anexado', 'Fechar', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      });
      return;
    }

    this.snackBar.open(
      `${anexados} de ${total} documentos salvos com sucesso!`,
      'Fechar',
      {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      }
    );
  }
}
